import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle, Info } from "lucide-react";
import { SPEC_DISCLAIMER, PLACEHOLDER_NOTE, NETWORK_DISCLAIMER } from "../content/site";

export function Pill({ label, tone = "cyan" }) {
  const styles =
    tone === "lime"
      ? "border-lime-600/50 bg-lime-50 text-lime-800"
      : "border-cyan-600/50 bg-cyan-50 text-cyan-900";
  return (
    <span
      data-testid={`pill-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-xs font-medium uppercase tracking-wide ${styles}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${tone === "lime" ? "bg-lime-500" : "bg-cyan-500"}`} aria-hidden="true" />
      {label}
    </span>
  );
}

export function SectionHead({ kicker, title, sub, id }) {
  return (
    <div className="max-w-3xl">
      {kicker && (
        <p className="font-mono text-xs font-medium uppercase tracking-widest text-primary" data-testid={id ? `${id}-kicker` : undefined}>{kicker}</p>
      )}
      <h2 className="mt-2 text-3xl font-semibold tracking-tight text-brand-navy sm:text-4xl">{title}</h2>
      {sub && <p className="mt-4 text-base leading-relaxed text-brand-slate md:text-lg">{sub}</p>}
    </div>
  );
}

export function PrimaryLink({ to, children, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="inline-flex items-center gap-2 rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-blue-700"
    >
      {children} <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </Link>
  );
}

export function GhostLink({ to, children, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="inline-flex items-center gap-2 rounded-sm border border-brand-navy/20 bg-white px-6 py-3 text-sm font-semibold text-brand-navy transition-colors duration-150 hover:border-primary hover:text-primary"
    >
      {children}
    </Link>
  );
}

export function SpecDisclaimer() {
  return (
    <div className="flex gap-3 rounded-sm border border-amber-300 bg-amber-50 p-4" data-testid="spec-disclaimer" role="note">
      <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />
      <p className="text-xs leading-relaxed text-amber-900">{SPEC_DISCLAIMER}</p>
    </div>
  );
}

export function PlaceholderNote({ text }) {
  return (
    <p className="flex items-start gap-2 rounded-sm border border-dashed border-brand-slate/40 bg-brand-surface p-3 text-xs text-brand-slate" data-testid="placeholder-note" role="note">
      <Info className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span>{text || PLACEHOLDER_NOTE}</span>
    </p>
  );
}

export function NetworkNote() {
  return (
    <div className="flex gap-3 rounded-sm border border-cyan-600/40 bg-cyan-50/60 p-4" data-testid="network-disclaimer" role="note">
      <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-700" aria-hidden="true" />
      <p className="text-xs leading-relaxed text-cyan-950">{NETWORK_DISCLAIMER}</p>
    </div>
  );
}

export function SpecTable({ specs, productId }) {
  return (
    <dl className="divide-y divide-border border border-border" data-testid={`spec-table-${productId}`}>
      {specs.map((s) => (
        <div key={s.k} className="grid grid-cols-1 gap-1 px-4 py-3 sm:grid-cols-3 sm:gap-4">
          <dt className="font-mono text-xs font-medium uppercase tracking-wide text-brand-slate">{s.k}</dt>
          <dd className="text-sm text-brand-navy sm:col-span-2">{s.v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function CTABand({ title, sub }) {
  return (
    <section className="border-t border-border bg-brand-navy" data-testid="cta-band">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        <h2 className="max-w-2xl text-3xl font-semibold tracking-tight text-white sm:text-4xl">
          {title || "Not sure which label fits your lanes?"}
        </h2>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-300">
          {sub || "Tell us about your shipments and we'll tell you honestly which architecture fits — and when neither does."}
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link
            to="/request-a-pilot"
            data-testid="cta-band-request-pilot"
            className="inline-flex items-center gap-2 rounded-sm bg-brand-cyan px-6 py-3 text-sm font-semibold text-brand-navy transition-colors duration-150 hover:bg-cyan-300"
          >
            Discuss Your Shipment <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <Link
            to="/compare-smart-labels"
            data-testid="cta-band-compare"
            className="inline-flex items-center gap-2 rounded-sm border border-white/30 px-6 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:border-brand-cyan hover:text-brand-cyan"
          >
            Compare the two labels
          </Link>
        </div>
      </div>
    </section>
  );
}
